import {
  Badge,
  IconButton,
  Stack,
  SvgIconProps,
  Typography,
} from "@mui/material";
import { memo } from "react";

interface SidebarTabButtonProps {
  label: string;
  icon: React.ComponentType<SvgIconProps>;
  badgeContent?: number;
  isSelected: boolean;
  isSidebarOpen?: boolean;
  onSelect: () => void;
  onOpenSidebar: () => void;
  onCloseSidebar: () => void;
}

/**
 * SidebarTabButton - Individual tab button in the sidebar nav
 *
 * Handles:
 * - Displaying icon with badge
 * - Toggling sidebar visibility
 * - Switching between tabs
 * - Visual highlighting when selected
 */
const SidebarTabButton = memo(
  ({
    label,
    icon: Icon,
    badgeContent = 0,
    isSelected,
    isSidebarOpen = false,
    onSelect,
    onOpenSidebar,
    onCloseSidebar,
  }: SidebarTabButtonProps) => {
    const handleClick = () => {
      if (isSelected) {
        // If already selected, close the sidebar
        onCloseSidebar();
      } else {
        // If not selected, switch tab and open sidebar if needed
        onSelect();
        if (!isSidebarOpen) {
          onOpenSidebar();
        }
      }
    };

    return (
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        gap={0.5}
      >
        {badgeContent > 0 ? (
          <Badge
            badgeContent={badgeContent}
            color="primary"
            max={99}
            sx={{
              "& .MuiBadge-anchorOriginTopRight": {
                right: 5,
                top: 5,
              },
            }}
          >
            <IconButton
              onClick={handleClick}
              color={isSelected ? "primary" : "default"}
              size="medium"
              data-testid={`aa-sidebar-tab-${label.toLowerCase()}`}
            >
              <Icon fontSize="medium" />
            </IconButton>
          </Badge>
        ) : (
          <IconButton
            onClick={handleClick}
            color={isSelected ? "primary" : "default"}
            size="medium"
            data-testid={`aa-sidebar-tab-${label.toLowerCase()}`}
          >
            <Icon fontSize="medium" />
          </IconButton>
        )}
        <Typography
          fontSize="10px"
          letterSpacing={0.5}
          textAlign="center"
          color={isSelected ? "primary" : "default"}
        >
          {label}
        </Typography>
      </Stack>
    );
  },
);

SidebarTabButton.displayName = "SidebarTabButton";

export default SidebarTabButton;
